import rootRoutes from "@app/_routes.json";
import bagsAndAccessoriesRoutes from "@app/[locale]/(site)/(listing)/bags-and-accessories/_routes.json";
import blackFridayRoutes from "@app/[locale]/(site)/(listing)/black-friday/_routes.json";
import campaignsRoutes from "../../app/[locale]/(site)/(editorial)/campaigns/[[...slug]]/_routes.json";
import checkoutRoutes from "@app/[locale]/(custom)/checkout/_routes.json";
import checkoutV2Routes from "@app/[locale]/(custom)/checkoutv2/_routes.json";
import collectionsRoutes from "@app/[locale]/(site)/(listing)/collections/_routes.json";
import createAccountRoutes from "@app/[locale]/(site)/(account)/create-account/_routes.json";
import createAccountVerificationRoutes from "@app/[locale]/(site)/(account)/create-account/verify/_routes.json";
import customerServiceRoutes from "@app/[locale]/(site)/customer-service/[...slug]/_routes.json";
import customerServiceLegalRoutes from "@app/[locale]/(site)/customer-service/legal-pages/[...slug]/_routes.json";
import cyberMondayRoutes from "@app/[locale]/(site)/(listing)/cyber-monday/_routes.json";
import exclusiveAccessRoutes from "@app/[locale]/(site)/(listing)/exclusive-access/_routes.json";
import exploreRoutes from "../../app/[locale]/(site)/(editorial)/explore/[[...slug]]/_routes.json";
import golfRoutes from "@app/[locale]/(site)/(listing)/golf/_routes.json";
import guidesRoutes from "../../app/[locale]/(site)/(editorial)/guides/[[...slug]]/_routes.json";
import eccoKollektiveRoutes from "../../app/[locale]/(site)/(editorial)/ecco-kollektive/[[...slug]]/_routes.json";
import eccoKollektiveCollectionsRoutes from "@app/[locale]/(site)/(listing)/ecco-kollektive-collections/_routes.json";
import kidsRoutes from "@app/[locale]/(site)/(listing)/kids/_routes.json";
import menRoutes from "@app/[locale]/(site)/(listing)/men/_routes.json";
import myAccountRoutes from "@app/[locale]/(site)/(account)/my-account/_routes.json";
import myAccountAddressesRoutes from "@app/[locale]/(site)/(account)/my-account/addresses/_routes.json";
import myAccountEccoClubRoutes from "@app/[locale]/(site)/(account)/my-account/ecco-club/_routes.json";
import myAccountOrdersRoutes from "@app/[locale]/(site)/(account)/my-account/orders/_routes.json";
import myAccountProfileRoutes from "@app/[locale]/(site)/(account)/my-account/profile/_routes.json";
import myAccountSubscriptionsRoutes from "@app/[locale]/(site)/(account)/my-account/subscriptions/_routes.json";
import myAccountWishlistRoutes from "@app/[locale]/(site)/(account)/my-account/wishlist/_routes.json";
import newRoutes from "@app/[locale]/(site)/(listing)/new/_routes.json";
import OrderTrackingRoutes from "@app/[locale]/(site)/(order)/order-tracking/_routes.json";
import orderRoutes from "@app/[locale]/(site)/(order)/order/_routes.json";
import orderConfirmationRoutes from "@app/[locale]/(site)/(order)/order/confirmation/_routes.json";
import outdoorRoutes from "@app/[locale]/(site)/(listing)/outdoor/_routes.json";
import productDetailRoutes from "@app/[locale]/(site)/product/_routes.json";
import saleRoutes from "@app/[locale]/(site)/(listing)/sale/_routes.json";
import shoeCareRoutes from "@app/[locale]/(site)/(listing)/shoe-care/_routes.json";
import storiesRoutes from "@app/[locale]/(site)/(editorial)/stories/[[...slug]]/_routes.json";
import womenRoutes from "@app/[locale]/(site)/(listing)/women/_routes.json";

export type PagePath = {
    en: string;
    [key: string]: string;
};

type GetPagePathByLocale = (pagePath: PagePath, locale?: string) => string;

export const getPagePathByLocale: GetPagePathByLocale = (pagePath, locale = "") =>
    pagePath?.[locale] ?? pagePath?.en;

export const getRouteFromRoutesFile = (
    routesObject: Record<string, PagePath>,
    routeName: string,
    additionalPath: PagePath = { en: "" }
): PagePath => {
    const paths: Record<string, PagePath> = {};

    for (const value of Object.values(routesObject)) {
        const decodedValue = Object?.fromEntries(
            Object?.entries(value)?.map(([locale, route]) => [
                locale,
                `${additionalPath?.[locale] || ""}/${decodeURIComponent(route)}`,
            ])
        ) as PagePath;
        paths[value?.en] = decodedValue;
    }

    return paths?.[routeName];
};

export const homePage: PagePath = {
    en: "/",
};

// Root pages
export const logInPage = getRouteFromRoutesFile(rootRoutes, "log-in");
export const searchPage = getRouteFromRoutesFile(rootRoutes, "search");
export const shoppingBagPage = getRouteFromRoutesFile(rootRoutes, "shopping-bag");
export const storeLocatorPage = getRouteFromRoutesFile(rootRoutes, "store-locator");
export const createAccountPage = getRouteFromRoutesFile(createAccountRoutes, "create-account");
export const createAccountVerificationPage = getRouteFromRoutesFile(
    createAccountVerificationRoutes,
    "verify",
    createAccountPage
);
export const resetPasswordPage = getRouteFromRoutesFile(rootRoutes, "reset-password");
export const unsubscribePage = getRouteFromRoutesFile(rootRoutes, "unsubscribe");

// My account pages
export const myAccountPage = getRouteFromRoutesFile(myAccountRoutes, "my-account");

export const myAccountWishlistPage = getRouteFromRoutesFile(
    myAccountWishlistRoutes,
    "wishlist",
    myAccountPage
);

export const myAccountAddressesPage = getRouteFromRoutesFile(
    myAccountAddressesRoutes,
    "addresses",
    myAccountPage
);

export const myAccountOrdersPage = getRouteFromRoutesFile(
    myAccountOrdersRoutes,
    "orders",
    myAccountPage
);

export const myAccountProfilePage = getRouteFromRoutesFile(
    myAccountProfileRoutes,
    "profile",
    myAccountPage
);

export const ECCO_CLUB_SLUG = "ecco-club";

export const myAccountEccoClubPage = getRouteFromRoutesFile(
    myAccountEccoClubRoutes,
    ECCO_CLUB_SLUG,
    myAccountPage
);

export const myAccountSubscriptionsPage = getRouteFromRoutesFile(
    myAccountSubscriptionsRoutes,
    "subscriptions",
    myAccountPage
);

// Kids pages
export const kidsCategoryPage = getRouteFromRoutesFile(kidsRoutes, "kids");

// Men pages
export const menCategoryPage = getRouteFromRoutesFile(menRoutes, "men");

// Collections pages
export const collectionsCategoryPage = getRouteFromRoutesFile(collectionsRoutes, "collections");

// Outdoor pages
export const outdoorCategoryPage = getRouteFromRoutesFile(outdoorRoutes, "outdoor");

// Black Friday pages
export const blackFridayPage = getRouteFromRoutesFile(blackFridayRoutes, "black-friday");

// Exclusive Access pages
export const exclusiveAccessPage = getRouteFromRoutesFile(
    exclusiveAccessRoutes,
    "exclusive-access"
);

// ECCO Kollektive Collections
export const eccoKollektiveCollectionsPage = getRouteFromRoutesFile(
    eccoKollektiveCollectionsRoutes,
    "ecco-kollektive-collections"
);

// Cyber Monday pages
export const cyberMondayPage = getRouteFromRoutesFile(cyberMondayRoutes, "cyber-monday");

//New pages
export const newPage = getRouteFromRoutesFile(newRoutes, "new");

// Editorial pages
export const campaignsPage = getRouteFromRoutesFile(campaignsRoutes, "campaigns");
export const storiesPage = getRouteFromRoutesFile(storiesRoutes, "stories");
export const explorePage = getRouteFromRoutesFile(exploreRoutes, "explore");
export const guidesPage = getRouteFromRoutesFile(guidesRoutes, "guides");
export const eccoKollektivePage = getRouteFromRoutesFile(eccoKollektiveRoutes, "ecco-kollektive");

// Checkout pages
export const checkoutPage = getRouteFromRoutesFile(checkoutRoutes, "checkout");
export const checkoutV2Page = getRouteFromRoutesFile(checkoutV2Routes, "checkoutv2");

// Golf pages
export const golfCategoryPage = getRouteFromRoutesFile(golfRoutes, "golf");

// Sale pages
export const saleCategoryPage = getRouteFromRoutesFile(saleRoutes, "sale");

// Product Detail pages
export const productDetailPage = getRouteFromRoutesFile(productDetailRoutes, "product");

// Women pages
export const womenCategoryPage = getRouteFromRoutesFile(womenRoutes, "women");

// Shoe Care pages
export const shoeCareCategoryPage = getRouteFromRoutesFile(shoeCareRoutes, "shoe-care");

// Bags and Accessories pages
export const bagsAndAccessoriesCategoryPage = getRouteFromRoutesFile(
    bagsAndAccessoriesRoutes,
    "bags-and-accessories"
);

// Customer Service pages
export const customerServicePages = getRouteFromRoutesFile(
    customerServiceRoutes,
    "customer-service"
);

export const customerServiceLegalPages = getRouteFromRoutesFile(
    customerServiceLegalRoutes,
    "legal-pages",
    customerServicePages
);

// Order pages
export const orderPage = getRouteFromRoutesFile(orderRoutes, "order");

export const orderConfirmationPage = getRouteFromRoutesFile(
    orderConfirmationRoutes,
    "confirmation",
    orderPage
);

export const orderTrackingPage = getRouteFromRoutesFile(OrderTrackingRoutes, "order-tracking");
